import React from 'react';

import { KlipyGifPicker } from '@/components/ui/media/providers/klipy/KlipyGifPicker';
import { KlipyGifView } from '@/components/ui/media/providers/klipy/KlipyGifView';
import type {
  IMediaItem,
  IMediaProvider,
  IMediaRenderOptions,
  IMediaShareContext,
} from '@/models/Media';
import { KlipyApi } from '@/services/media/providers/klipy/api';
import { mediaRegistry } from '@/services/media/registry';

// Module-level config kept in sync with the React tree by `MediaConfigSync`.
// This lets `isConfigured()` and `triggerShare()` work without any caller
// (composer, comment sheet, conversation screen) needing to know about Klipy.
let activeApiKey: string | undefined;

const isApiKeyUsable = (apiKey: string | undefined): apiKey is string =>
  !!apiKey && !apiKey.startsWith('REPLACE_WITH_');

const KLIPY_DISPLAY_NAME = 'KLIPY';

export const configureKlipyProvider = (config: { apiKey?: string }) => {
  activeApiKey = config.apiKey;
};

const klipyProvider: IMediaProvider = {
  key: 'klipy',
  displayName: KLIPY_DISPLAY_NAME,
  supportedTypes: ['gif'],
  isConfigured: () => isApiKeyUsable(activeApiKey),
  PickerComponent: KlipyGifPicker,
  renderItem: (item: IMediaItem, options?: IMediaRenderOptions) => {
    if (!item.Id) return null;
    return (
      <KlipyGifView
        slug={item.Id}
        rounded={options?.rounded ?? true}
        maxWidth={options?.maxWidth}
      />
    );
  },
  triggerShare: async (item: IMediaItem, ctx?: IMediaShareContext) => {
    if (!item.Id || !isApiKeyUsable(activeApiKey)) return;

    try {
      await KlipyApi.gifs.triggerShare(activeApiKey, item.Id, ctx?.query);
    } catch {
      // Fire-and-forget analytics call.
    }
  },
};

let isRegistered = false;

export const registerKlipyProvider = () => {
  if (isRegistered) return;
  mediaRegistry.register(klipyProvider);
  isRegistered = true;
};
